import { cn } from "@/lib/utils";
import { Sparkles, Crown } from "lucide-react";

export type PersonaKey = "syra" | "jarvis";

const PERSONAS: Record<
  PersonaKey,
  { name: string; tagline: string; icon: typeof Sparkles }
> = {
  syra: {
    name: "Syra",
    tagline: "Acolhedora · voz feminina (Sarah)",
    icon: Sparkles,
  },
  jarvis: {
    name: "J.A.R.V.I.S.",
    tagline: "Mordomo formal · voz masculina (Daniel)",
    icon: Crown,
  },
};

interface Props {
  value: PersonaKey;
  onChange: (v: PersonaKey) => void;
  disabled?: boolean;
  className?: string;
}

export function PersonaSelector({ value, onChange, disabled, className }: Props) {
  return (
    <div
      role="radiogroup"
      aria-label="Persona do assistente"
      className={cn(
        "grid grid-cols-2 gap-2 p-1 rounded-xl bg-muted/40 border border-border/60",
        className,
      )}
    >
      {(Object.keys(PERSONAS) as PersonaKey[]).map((k) => {
        const p = PERSONAS[k];
        const Icon = p.icon;
        const active = value === k;
        return (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => onChange(k)}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active
                ? "bg-background shadow-sm border border-border"
                : "hover:bg-background/60 border border-transparent",
              disabled && "opacity-50 cursor-not-allowed",
            )}
          >
            <span
              className={cn(
                "flex items-center justify-center w-9 h-9 rounded-md shrink-0",
                active ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
              )}
            >
              <Icon className="w-4 h-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold leading-tight truncate">
                {p.name}
              </span>
              <span className="block text-[11px] text-muted-foreground leading-tight truncate">
                {p.tagline}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

const STORAGE_KEY = "jarvis.persona_key";

export function loadPersonaKey(): PersonaKey {
  if (typeof window === "undefined") return "syra";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "jarvis" || v === "syra" ? v : "syra";
}

export function hasStoredPersonaKey(): boolean {
  if (typeof window === "undefined") return false;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "jarvis" || v === "syra";
}

export function savePersonaKey(v: PersonaKey) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, v);
}
